/**
 * Sistema de caché para evitar la creación duplicada de objetos
 * durante la auto-configuración de tienda y reservas
 */

interface ObjectCreationCacheEntry {
  apiName: string;
  extended: string;
  objectId: string;
  timestamp: number;
  clientId: string;
}

class ObjectCreationCache {
  private cache: Map<string, ObjectCreationCacheEntry> = new Map();
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutos

  /**
   * Genera una clave única para el caché basada en clientId, apiName y extended
   */
  private getCacheKey(clientId: string, apiName: string, extended: string): string {
    return `${clientId}:${apiName}:${extended}`;
  }

  /**
   * Verifica si un objeto está en el caché y es válido
   */
  has(clientId: string, apiName: string, extended: string): boolean {
    const key = this.getCacheKey(clientId, apiName, extended);
    const entry = this.cache.get(key);
    
    if (!entry) {
      return false;
    }
    
    // Verificar si el caché ha expirado
    const now = Date.now();
    if (now - entry.timestamp > this.CACHE_DURATION) {
      this.cache.delete(key);
      return false;
    }
    
    return true;
  }

  /**
   * Obtiene el objectId del caché si existe y es válido
   */
  get(clientId: string, apiName: string, extended: string): string | null {
    if (!this.has(clientId, apiName, extended)) {
      return null;
    }
    
    const key = this.getCacheKey(clientId, apiName, extended);
    const entry = this.cache.get(key);
    return entry?.objectId || null;
  }

  /**
   * Agrega un objeto al caché
   */
  set(clientId: string, apiName: string, extended: string, objectId: string): void {
    const key = this.getCacheKey(clientId, apiName, extended);
    const entry: ObjectCreationCacheEntry = {
      apiName,
      extended,
      objectId,
      timestamp: Date.now(),
      clientId,
    };
    
    this.cache.set(key, entry);
    console.log('[ObjectCreationCache] Objeto agregado al caché');
  }

  /**
   * Limpia el caché completo
   */
  clear(): void {
    this.cache.clear();
    console.log('🗑️ Caché de objetos limpiado');
  }

  /**
   * Limpia entradas expiradas del caché
   */
  cleanExpired(): void {
    const now = Date.now();
    let cleaned = 0;
    
    for (const [key, entry] of this.cache.entries()) {
      if (now - entry.timestamp > this.CACHE_DURATION) {
        this.cache.delete(key);
        cleaned++;
      }
    }
    
    if (cleaned > 0) {
      console.log('[ObjectCreationCache] Entradas expiradas eliminadas');
    }
  }

  /**
   * Obtiene el tamaño actual del caché
   */
  size(): number {
    return this.cache.size;
  }

  /**
   * Obtiene todas las entradas del caché (para debugging)
   */
  getAll(): ObjectCreationCacheEntry[] {
    return Array.from(this.cache.values());
  }
}

// Instancia singleton del caché
export const objectCreationCache = new ObjectCreationCache();

// Limpiar entradas expiradas cada minuto
if (typeof window !== 'undefined') {
  setInterval(() => {
    objectCreationCache.cleanExpired();
  }, 60 * 1000);
}
